/**
 * Provides APIs that helps analytics entities or actions perform data interactions with the PRPC server.
 * @module AnalyticsUtils
 */
/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import type { AxiosResponse } from 'axios';

import { resolveRestApi } from '../router/app-router';
import { fetch as axiosFetch } from '../service_broker/fetch';
import DataApiUtils from '../data-view/data-api-utils';
import Utils from '../utils/utils';
import {
  getDataPageMetadata,
  getInsight,
  getFieldsByClassName,
  getEmbeddedPropertyMetadata
} from '../rules/rules-store';
import DataApi, { type Config } from '../data-view';
import type { DataResponse } from '../data-view/types';
import type { Field } from '../rules/types';

import type { DataObjectMeta, DataObjectsAPIResponse, DefaultColumn } from './types/data-model';
import type { InfinityInsights, LaunchpadRuntimeInsights } from './types/insights';

const GET_DATA_OBJECTS = 'getDataObjects';
const GET_DATA_PAGE_OBJECTS = 'getDataPageObjects';
const GET_DATA_PAGES = 'getDataPages';
const GET_LIST_DATA = 'getListData';
const GET_INSIGHTS = 'getInsightIDs';
const GET_INSIGHT_BY_ID = 'getInsightByID';
const CREATE_INSIGHT = 'createInsight';
const DELETE_INSIGHT = 'deleteInsight';
const EXPORT_TO_EXCEL = 'exportToExcel';
const EXPORT_TO_CSV = 'exportToCSV';
const SCHEDULE_INSIGHT = 'scheduleInsight';
const UPDATE_SCHEDULED_INSIGHT = 'updateScheduledInsight';
const DELETE_SCHEDULED_INSIGHT = 'deleteScheduledInsight';
const TRANSLATE_STRINGS = 'translateStrings';
const UPDATE_INSIGHT = 'updateInsight';
const tableTypeListValues = ['LocalList', 'RemoteList', 'PromptList', 'DataPage'];

let dataObjectsPromise: Promise<AxiosResponse<DataObjectsAPIResponse>> | null = null; // Used to avoid multiple parallel requests (see BUG-702567)
/**
 * Retrieves the list of data objects in the current application.
 * @param useCache whether to use session cache or not.
 * @function getDataPageObjects
 * @returns Resolves to a response containing data objects corresponding to data pages.
 */
export function getDataPageObjects(useCache = true): Promise<AxiosResponse<DataObjectsAPIResponse>> {
  // If another request is underway, return the promise from that request
  if (dataObjectsPromise) return dataObjectsPromise;

  const cacheKey = DataApiUtils.getAppCacheKey(GET_DATA_PAGE_OBJECTS);
  if (useCache && window.sessionStorage.getItem(cacheKey)) {
    return Promise.resolve(JSON.parse(`${window.sessionStorage.getItem(cacheKey)}`));
  }

  const reqPayload = { datapageRequestType: 'explorable' };
  const { restApi, reqType } = resolveRestApi(GET_DATA_PAGES, reqPayload);
  dataObjectsPromise = axiosFetch(restApi, {
    method: reqType,
    datapageRequestType: 'explorable'
  });

  dataObjectsPromise.then((results: AxiosResponse<DataObjectsAPIResponse>) => {
    // Reset so that any subsequent calls with useCache:false will make a new request
    dataObjectsPromise = null;
    // Cache it even when useCache is false, so that a subsequent call with useCache:true can benefit
    window.sessionStorage.setItem(cacheKey, JSON.stringify(results));
  });

  return dataObjectsPromise;
}

/**
 * Retrieves the list of data objects in the current application.
 * @function getDataObjects
 * @returns Resolves to a response containing data objects corresponding to data types and case types.
 *
 * @example  <caption>In this example, the API returns a Promise that resolves to a response containing the list of data objects.</caption>
 * PCore.getAnalyticsUtils().getDataObjects().then(response => { console.log(response.data); }).catch(() => { ... });
 * // console output:[{
 * //   classID: "OPB1HW-MyApp-Work-MyCase",
 * //   defaultListViewData: "D_MyCaseList",
 * //   (other properties like: description, name, links)
 * // }]
 */
export function getDataObjects(): Promise<AxiosResponse<DataObjectsAPIResponse>> {
  const cacheKey = DataApiUtils.getAppCacheKey(GET_DATA_OBJECTS);
  if (window.sessionStorage.getItem(cacheKey)) {
    return Promise.resolve(JSON.parse(`${window.sessionStorage.getItem(cacheKey)}`));
  }
  const { restApi, reqType } = resolveRestApi(GET_DATA_OBJECTS, {});
  const dataObjsPromise = axiosFetch(restApi, {
    method: reqType
  });
  dataObjsPromise.then((results: AxiosResponse<DataObjectsAPIResponse>) => {
    window.sessionStorage.setItem(cacheKey, JSON.stringify(results));
  });
  return dataObjsPromise;
}

/**
 * Retrieves the default columns for a table displaying case data.
 * @function getDefaultColumns
 * @param payload A payload object that will specify the report to retrieve the columns from for use as the "default fields" of a data entity.
 * @param payload.reportName The name of the report in which the default columns are configured.
 * @param payload.className The class of the report in which the default columns are configured
 * @returns Resolves to a response containing info for the default columns.
 *
 * @example  <caption>In the example below, the API returns a Promise that resolves to a response containing information for the default columns.</caption>
 * const payload = { className: "OZ1CUU-MyApp-Work-MyCase", reportName: "DataTableEditorReport" };
 * PCore.getAnalyticsUtils().getDefaultColumns(payload).then(response => { console.log(response.data); }).catch(() => { ... });
 * // console output:[
 * //   { pyFieldName: "pzInsKey" },
 * //   { pyFieldName: "pyID" },
 * //   { pyFieldName: "columnName" }
 * // ]
 */
export function getDefaultColumns(payload: {
  reportName: string;
  className: string;
}): Promise<AxiosResponse<DefaultColumn[] | undefined>> {
  const cacheKey = DataApiUtils.getAppCacheKey(`DefaultColumns_${Object.values(payload).join('_')}`);
  if (window.sessionStorage.getItem(cacheKey)) {
    return Promise.resolve(JSON.parse(`${window.sessionStorage.getItem(cacheKey)}`));
  }
  const reqPayload = {
    dataViewName: 'D_pzGetDefaultColumns'
  };
  const { restApi, reqType } = resolveRestApi(GET_LIST_DATA, reqPayload);
  const defaultColumnsPromise = axiosFetch(restApi, {
    method: reqType,
    body: {
      dataViewParameters: payload
    }
  });
  defaultColumnsPromise.then((results: AxiosResponse<DefaultColumn[] | undefined>) => {
    window.sessionStorage.setItem(cacheKey, JSON.stringify(results));
  });

  return defaultColumnsPromise;
}

/**
 * Fetches the metadata of the given dataView from rule store
 * @param dataViewName - dataViewName name
 * @returns Includes data view metadata and fields metadata
 * @private
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function getDataViewMetadataFromRuleStore(dataViewName: string) {
  const sourceMetadata = getDataPageMetadata(dataViewName);
  if (sourceMetadata && sourceMetadata.classID) {
    let sourceFields = getFieldsByClassName(sourceMetadata.classID);

    // Get embeddedFields from rdFields and prepare embeddedFieldsMetadata
    const embeddedFields = (sourceMetadata.rdFields as { fields: string[] })?.fields?.filter(
      (field: string) => field.indexOf('.') > 0
    );
    if (embeddedFields && !Utils.isEmpty(embeddedFields)) {
      const embeddedFieldsMetadata = embeddedFields.reduce((acc: { [key: string]: Field }, currField: string) => {
        const metadata = getEmbeddedPropertyMetadata(currField, sourceMetadata.classID);
        acc[currField] = metadata as Field;
        return acc;
      }, {});

      sourceFields = { ...sourceFields, ...embeddedFieldsMetadata } as unknown as { [key: string]: Field };
    }

    if (sourceFields && Object.keys(sourceFields).length > 0) {
      for (const k of Object.keys(sourceFields)) {
        // filter out 'Page' and 'Page list' properties
        if (sourceFields[k].type.startsWith('Page')) {
          delete sourceFields[k];
        } else if (sourceFields[k]?.datasource && tableTypeListValues.includes(sourceFields[k].datasource.tableType)) {
          sourceFields[k].fieldType = 'Picklist';
        }
      }
      return { sourceFields, sourceMetadata };
    }
  }
  return {};
}

/**
 * This API retrieves the columns configured on the RD which is bound to the given data view.
 * @function getFieldsForDataSource
 * @param dataViewName - dataViewName name
 * @param skipStoreCheck - The flag that determines if the fields of the given data view must be fetched from the rule store or from the browser cache.
 * Set skipStoreCheck to true if the fields of the given data view must be fetched from the browser cache.
 * Set skipStoreCheck to false if the fields of the given data view must be fetched from the rule store.
 * If the fields are not found in the rule store, it is fetched from the browser cache.
 * @param contextName name of the context.
 * @returns list of columns configured on RD bound on the data view
 *
 * @example  <caption>In the example below, the API returns a Promise that resolves to a response containing list of RD columns.</caption>
 * const dataViewName = "D_MyCaseList"
 * PCore.getAnalyticsUtils().getFieldsForDataSource(dataViewName, false, 'app/Primary_1');
 * //The response of this api will be like shown below.
  {
    "data": [
      { pyFieldName: "pzInsKey" },
      { pyFieldName: "pyID" },
      { pyFieldName: "columnName" }
    ]
  }
 */
export function getFieldsForDataSource(
  dataViewName: string,
  skipStoreCheck: boolean,
  contextName: string
): Promise<object> {
  if (!skipStoreCheck) {
    const sourceMetadata = getDataPageMetadata(dataViewName);
    const rdFields = !Array.isArray(sourceMetadata?.rdFields)
      ? sourceMetadata?.rdFields?.fields
      : sourceMetadata?.rdFields;
    if (rdFields) {
      return Promise.resolve({
        data: { data: rdFields.map((k: string) => ({ pyFieldName: k })) }
      });
    }
    return Promise.resolve({
      data: { data: [] }
    });
  }

  const cacheKey = DataApiUtils.getAppCacheKey(`RDColumns-${dataViewName}`);
  if (window.sessionStorage.getItem(cacheKey)) {
    return Promise.resolve(JSON.parse(`${window.sessionStorage.getItem(cacheKey)}`));
  }
  const rdColumnsPromise = DataApiUtils.getData(
    'D_pxFieldsForDataSource',
    {
      dataViewParameters: { DataPageName: dataViewName }
    },
    contextName
  );

  rdColumnsPromise.then((results: DataResponse) => {
    window.sessionStorage.setItem(cacheKey, JSON.stringify(results));
  });
  return rdColumnsPromise;
}

/**
 * Retrieves the the primary fields for the given dataview
 * @function getPrimaryFieldsForDataSource
 * @param dataViewName - dataViewName name
 * @param dataViewClassName - class name
 * If Primary Fields are not found, defaults to listing Fields
 * @returns list of primary fields or fields
 */
export function getPrimaryFieldsForDataSource(dataViewName = '', dataViewClassName = ''): Promise<DataResponse> {
  const cacheKey = DataApiUtils.getAppCacheKey(`PFColumns-${dataViewName}${dataViewClassName}`);
  if (window.sessionStorage.getItem(cacheKey)) {
    return Promise.resolve(JSON.parse(`${window.sessionStorage.getItem(cacheKey)}`));
  }
  const rdColumnsPromise = DataApiUtils.getData('D_pxGetInitialFieldsForDataSource', {
    dataViewParameters: { DataPageName: dataViewName, ClassName: dataViewClassName }
  });

  rdColumnsPromise.then((results: DataResponse) => {
    window.sessionStorage.setItem(cacheKey, JSON.stringify(results));
  });
  return rdColumnsPromise;
}

// Keeps track of active API calls for Primary fields
// Used to avoid duplicate API calls for the same class
const primaryFieldPromisesByClass: { [classID: string]: Promise<void> | undefined } = {};

// Returns PrimaryFields cacheKey for a given classID
const getPrimaryFieldsCacheKey = (classID: string) => DataApiUtils.getAppCacheKey(`PrimaryFields-${classID}`);

// Makes an API call to fetch Primary fields for the given list of classes
// Caches the response to sessionStorage separately for each class
const getPrimaryFieldsFromAPI = async (
  allClassIDs: string[],
  topLevelClassID?: string,
  topLevelDataPageID?: string,
  joinedClassIDs?: string[]
): Promise<void> => {
  const primaryFieldsPromise = DataApiUtils.getData('D_pxGetPrimaryFieldsForInsights', {
    dataViewParameters: {
      ...(topLevelClassID && { topLevelClassID }),
      ...(joinedClassIDs && { joinedClassIDs: joinedClassIDs.join(',') }),
      ...(topLevelDataPageID && { topLevelDataPageID })
    }
  })
    .then((res) => {
      // This can be null if all the input classes do NOT have PrimaryFields defined
      const response = res.data?.data || [];

      // The API returns a single array of primary fields from all classes
      // Group them by classID
      const classWisePrimaryFields: { [classID: string]: string[] } = {};
      allClassIDs.forEach((classID) => {
        // If a class doesn't have primary fields defined yet, the API will skip that class in response.
        // Cache an empty array for that class so that we don't send further calls
        classWisePrimaryFields[classID] = [];
      });
      response.forEach(({ classID, pyFieldName }) => {
        classWisePrimaryFields[classID].push(pyFieldName);
      });

      // Cache each class to sessionStorage
      Object.entries(classWisePrimaryFields).forEach(([classID, primaryFields]) => {
        const cacheKey = getPrimaryFieldsCacheKey(classID);
        window.sessionStorage.setItem(cacheKey, JSON.stringify(primaryFields));
      });
    })
    .finally(() => {
      allClassIDs.forEach((classID) => {
        delete primaryFieldPromisesByClass[classID];
      });
    });

  allClassIDs.forEach((classID) => {
    primaryFieldPromisesByClass[classID] = primaryFieldsPromise;
  });

  return primaryFieldsPromise;
};

// Returns available primary fields info from session and other classes information which needs API call
function fillAvailablePrimaryFieldsFromSession(topLevelClassID?: string, joinedClassIDs?: string[]) {
  // Determine which all of these classes need a new API call
  const classesToFetch: string[] = [];
  const promisesToAwait: Promise<void>[] = [];
  let isTopLevelClassPresent = false;
  const joinedClassesToFetch: string[] = [];
  const allClasses: string[] = [];

  if (topLevelClassID) {
    allClasses.push(topLevelClassID);
  }
  if (joinedClassIDs && joinedClassIDs.length > 0) {
    allClasses.push(...joinedClassIDs);
  }

  allClasses.forEach((classID) => {
    // Check if PrimaryFields for this class are already fetched and cached to sessionStorage
    const cacheKey = getPrimaryFieldsCacheKey(classID);
    if (window.sessionStorage.getItem(cacheKey)) {
      return;
    }

    // If an API call is already underway for this class, wait for that promise as well
    // By the time that promise is resolved, the PrimaryFields would be available on sessionStorage
    if (primaryFieldPromisesByClass[classID]) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      promisesToAwait.push(primaryFieldPromisesByClass[classID]!);
      return;
    }

    // Otherwise, make an API call for this class
    classesToFetch.push(classID);

    if (classID === topLevelClassID) {
      isTopLevelClassPresent = true;
    } else {
      joinedClassesToFetch.push(classID);
    }
  });

  return {
    allClasses,
    promisesToAwait,
    classesToFetch,
    isTopLevelClassPresent,
    joinedClassesToFetch
  };
}

/**
 * Retrieves the primary fields for the given list of classes
 * NOTE: It skips returning classes in the response object if the API calls failed for any reason
 * or if PrimaryFields were never defined for a given class
 *
 * @function getPrimaryFieldsForInsights
 * @param {string[]} [topLevelClassID] - Top level classID for which, primary fields need to be fetched
 * @param {string} [topLevelDataPageID] - Data page ID needed to fallback to RD fields if primary fields were not set
 * @param {string[]} [joinedClassIDs] - IDs of classes for which, primary fields need to be fetched
 * @returns {object} An object with classIDs as keys and array of primary fields as values
 * @private
 */
export async function getPrimaryFieldsForInsights(
  topLevelClassID?: string,
  topLevelDataPageID?: string,
  joinedClassIDs?: string[]
) {
  const { allClasses, promisesToAwait, classesToFetch, isTopLevelClassPresent, joinedClassesToFetch } =
    fillAvailablePrimaryFieldsFromSession(topLevelClassID, joinedClassIDs);

  // If there are any classes left, make an API call for them
  if (classesToFetch.length) {
    promisesToAwait.push(
      getPrimaryFieldsFromAPI(
        classesToFetch,
        isTopLevelClassPresent ? topLevelClassID : undefined,
        isTopLevelClassPresent ? topLevelDataPageID : undefined,
        joinedClassesToFetch.length > 0 ? joinedClassesToFetch : undefined
      )
    );
  }

  // Wait for all active API calls
  return Promise.allSettled(promisesToAwait).then(() => {
    // By this point, we expect PrimaryFields for all classes to be fetched and cached to sessionStorage
    // If any of the API calls errored out, we simply don't set PrimaryFields for those
    const data: { [classID: string]: string[] } = {};

    allClasses.forEach((classID) => {
      const cacheKey = getPrimaryFieldsCacheKey(classID);
      if (window.sessionStorage.getItem(cacheKey)) {
        try {
          data[classID] = JSON.parse(`${window.sessionStorage.getItem(cacheKey)}`);
        } catch {
          // Simply skip those classes if the cached value is corrupted for whatever reason
        }
      }
    });

    return { data };
  });
}

function associationLabel(entry: [string, Field]) {
  let label;
  if (entry[1].category) label = entry[1].category;
  else if (entry[0].indexOf(':') > 0) label = entry[0].substring(0, entry[0].indexOf(':'));
  return label;
}

/**
 * This API obtains the metadata of a data view as a Promise and caches the response in session storage.
 * @function getDataViewMetadata
 * @param dataViewName - dataViewName name
 * @param skipStoreCheck - The flag that determines if the metadata of the given data view must be fetched from the rule store or from the metadata API
 * Note:
 * - The default value is false.
 * - Set `skipStoreCheck` to true if the metadata of the given data view must be fetched from the /metadata API call
 * - Set `skipStoreCheck` to false if the metadata of the given data view must be fetched from rules store. if its not found on that then fetch it from /metadata API call

 * @returns metadata object for the given dataView
 *
 * @example  <caption>In the example below, the API returns the metadata of the data view whose name is 'D_BugList'.</caption>
 *
 * const dataViewName = "D_BugList"
 * PCore.getAnalyticsUtils().getDataViewMetadata(dataViewName);
 *
 * //The response of this api will be like shown below.
 {
  "data" : {
    "classID": "PegaProjMgm-Work-Bug",
    "className": "Bug",
    "structure": "List",
    "isQueryable": true,
    "fields": [
        {
        "description": "operator who manager assigns work to",
        "fieldID": "pyAssignedOperator",
        "fieldType": "Identifier",
        "isReadOnly": false,
        "name": "Assigned To",
        "dataType": "Identifier"
        },
        {
        "description": "This property is used to identify the work object's parent
        Backlog and should include the pyID of that work object. In the future,
        it will be derived from the user's data input into UserStoryIDEntry or
        based upon the context of the creation of the item.",
        "displayAs": "pxTextInput",
        "fieldID": "BacklogID",
        "fieldType": "Text (single line)",
        "isReadOnly": false,
        "name": "Backlog ID",
        "maxLength": 32,
        "dataType": "Text"
        }
    ]
  }
}
*/
export function getDataViewMetadata(
  dataViewName: string,
  skipStoreCheck: boolean,
  associationFilter?: string[] | null
): Promise<{ data: DataObjectMeta }> {
  if (!skipStoreCheck) {
    const { sourceMetadata, sourceFields } = getDataViewMetadataFromRuleStore(dataViewName);
    if (sourceMetadata && sourceFields && Object.keys(sourceFields).length > 0) {
      const responseObj = {
        data: {
          ...sourceMetadata,
          fields: Object.entries(sourceFields).map((entry) => {
            return {
              ...entry[1],
              fieldID: entry[0],
              fieldType: entry[1].fieldType || (entry[1].isUserReference ? 'User reference' : undefined),
              category: associationLabel(entry),
              classID: entry[1].associationClassID ? entry[1].associationClassID : entry[1].classID
            };
          })
        } as unknown as DataObjectMeta
      };
      return Promise.resolve(responseObj);
    }
  }
  const config: Config = {
    dataSource: dataViewName,
    columns: [],
    parameters: {},
    associationFilter,
    matchPosition: '',
    ignoreCase: false
  };
  return DataApi.init(config).then(({ dataSourceMetaData: data }) => {
    return Promise.resolve({ data } as { data: DataObjectMeta });
  });
}

/**
 * Retrieves all available insight IDs.
 * @function getInsightIDs
 * @returns Resolves to a response containing a list of insights with the related information.
 *
 * @example  <caption>In this example, the API returns a Promise that resolves to a response containing a list of insight with the related information</caption>
 * PCore.getAnalyticsUtils().getInsightIDs().then(response => { console.log(response.data); }).catch(() => { ... });
 * // console output: {
 * //   pyInsights: [{
 * //     pyID: "124e9385-a623-4c55-ba8e-5af8cbd0ae64",
 * //     pyName: "Saved Insight"
 * //     pyCreateDateTime: "20200630T183653.784 GMT",
 * //     pyCreateOperator: "user@pega.com",
 * //     pyUpdateDateTime: "20200630T183656.330 GMT",
 * //     pyUpdateOperator: "user@pega.com",
 * //     pxObjClass: "PegaBI-API-Insight"
 * //   }]
 * // }
 */
export function getInsightIDs(): Promise<object> {
  const { restApi, reqType } = resolveRestApi(GET_INSIGHTS);
  return axiosFetch(restApi, {
    method: reqType
  });
}

/**
 * Retrieves the metadata related to the given insight ID.
 * @function getInsightByID
 * @param insightID The unique ID for the insight you are trying to retrieve.
 * @returns Resolves to a response containing the metadata for the given insight.
 *
 * @example  <caption>In the example below, the API returns a Promise that resolves to a response containing the metadata for the specified insight.</caption>
 * const insightID = "124e9385-a623-4c55-ba8e-5af8cbd0ae64";
 * PCore.getAnalyticsUtils().getInsightByID(insightID).then(response => { console.log(response.data); }).catch(() => { ... });
 * // console output: {
 * //   pyInsights: [{
 * //     pyContent: "[stringified insight metadata json]",
 * //     pyName: "Insight Name",
 * //     pyPermissions: [{
 * //       pyAccessCategory: "Rule-Access-Role-Name",
 * //       pyAccessType: "view",
 * //       pyAccessValue: "TestApp:Users"
 * //     }]
 * //   }]
 * // }
 */
export function getInsightByID(insightID: string): Promise<object> {
  const insight = getInsight(insightID);
  if (insight) {
    return Promise.resolve({
      data: {
        pyInsights: [insight]
      }
    });
  }
  const reqPayload = { insightID };
  const { restApi, reqType } = resolveRestApi(GET_INSIGHT_BY_ID, reqPayload);
  return axiosFetch(restApi, {
    method: reqType
  });
}

/**
 * Retrieves the metadata related to the given insight IDs.
 * @function getInsightsByID
 * @param insightIDs The unique IDs for the insights you are trying to retrieve.
 * @returns Resolves to a response containing the metadata for the given insight.
 * @private
 *
 * @example  <caption>In the example below, the API returns a Promise that resolves to a response containing the metadata for the specified insight.</caption>
 * const insightIDs = ["124e9385-a623-4c55-ba8e-5af8cbd0ae64", "b15cca34-e61b-468a-b1ee-f8492ada78e4"];
 * PCore.getAnalyticsUtils().getInsightsByID(insightIDs).then(response => { console.log(response.data); }).catch(() => { ... });
 * // console output: {
 * //   pyInsights: [{
 * //     pyContent: "[stringified insight metadata json]",
 * //     pyName: "Insight Name",
 * //     pyPermissions: [{
 * //       pyAccessCategory: "Rule-Access-Role-Name",
 * //       pyAccessType: "view",
 * //       pyAccessValue: "TestApp:Users"
 * //     }]
 * //   },{
 * //     pyContent: "[stringified insight metadata json]",
 * //     pyName: "Insight Name",
 * //     pyPermissions: [{
 * //       pyAccessCategory: "Rule-Access-Role-Name",
 * //       pyAccessType: "view",
 * //       pyAccessValue: "TestApp:Users"
 * //     }]
 * //   }]
 * // }
 */
export function getInsightsByID(insightIDs: string[]) {
  const reqPayload = { insightID: insightIDs.join(',') };
  const { restApi, reqType } = resolveRestApi(GET_INSIGHT_BY_ID, reqPayload);

  return axiosFetch(restApi, {
    method: reqType
  });
}

/**
 * Creates a new insight rule containing the provided metadata.
 * @function createInsight
 * @param insightObject The metadata you want to store in an insight rule.
 * @returns Resolves to a response containing the ID for the new insight.
 * @private
 *
 * @example  <caption>The API creates a new insight rule and returns a Promise that resolves to a response containing top-level info for the insight.</caption>
 * const insightObject = {
 *   pyContent: "[stringified insight metadata json]",
 *   pyName: "Insight Name",
 *   pyPermissions: [{
 *     pyAccessCategory: "Rule-Access-Role-Name",
 *     pyAccessType: "view", // or "edit"
 *     pyAccessValue: "TestApp:Users"
 *   }]
 * };
 * PCore.getAnalyticsUtils().createInsight(insightObject).then(response => { console.log(response.data); }).catch(() => { ... });
 * // console output: {
 * //   pyID: "124e9385-a623-4c55-ba8e-5af8cbd0ae64",
 * //   pyName: "Insight Name",
 * //   pyCreateDateTime: "20200630T183653.784 GMT",
 * //   pyCreateOperator: "user@pega.com",
 * //   pyUpdateDateTime: "20200630T183656.330 GMT",
 * //   pxObjClass: "PegaBI-API-Insight"
 * // }
 */
export function createInsight(
  insightObject: InfinityInsights.CreateInsightRequestBody | LaunchpadRuntimeInsights.CreateInsightRequestBody
): Promise<AxiosResponse<Partial<InfinityInsights.ResponseInsight> | Partial<LaunchpadRuntimeInsights.Insight>>> {
  const { restApi, reqType } = resolveRestApi(CREATE_INSIGHT);
  return axiosFetch(restApi, {
    method: reqType,
    body: JSON.stringify(insightObject),
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

/**
 * Update an existing insight rule.
 * @param insightID The ID of the insight rule to update.
 * @param insightObject The insight metadata to save.
 * @private
 *
 * @example <caption>The API updates an existing insight rule and returns a Promise that resolves to a response containing top-level info for the insight.</caption>
 * const insightID = "124e9385-a623-4c55-ba8e-5af8cbd0ae64";
 * const insightObject = {
 *   pyContent: "[stringified insight metadata json]",
 *   pyName: "Insight Name",
 *   pyPermissions: [{
 *     pyAccessCategory: "Rule-Access-Role-Name",
 *     pyAccessType: "view", // or "edit"
 *     pyAccessValue: "TestApp:Users"
 *   }]
 * };
 * PCore.getAnalyticsUtils().updateInsight(insightID, insightObject).then(response => { console.log(response.data); }).catch(() => { ... });
 * // console output: {
 * //   pyID: "124e9385-a623-4c55-ba8e-5af8cbd0ae64",
 * //   pyName: "Insight Name",
 * //   pyCreateDateTime: "20200630T183653.784 GMT",
 * //   pyCreateOperator: "user@pega.com",
 * //   pyUpdateDateTime: "20200630T183656.330 GMT",
 * //   pxObjClass: "PegaBI-API-Insight"
 * // }
 */
export function updateInsight(
  insightID: string,
  insightObject: InfinityInsights.UpdateInsightRequestBody | LaunchpadRuntimeInsights.UpdateInsightRequestBody
): Promise<AxiosResponse<Partial<InfinityInsights.ResponseInsight> | Partial<LaunchpadRuntimeInsights.Insight>>> {
  const reqPayload = { insightID };
  const { restApi, reqType } = resolveRestApi(UPDATE_INSIGHT, reqPayload);
  let etag;

  if ('UpdateDateTime' in insightObject) {
    etag = insightObject.UpdateDateTime; // only available in launchpad
  }

  return axiosFetch(restApi, {
    method: reqType,
    body: JSON.stringify(insightObject),
    headers: {
      'if-match': etag,
      'Content-Type': 'application/json'
    }
  });
}

/**
 * Delete an existing insight rule.
 * @function deleteInsight
 * @param insightID The ID of the insight rule to delete.
 * @returns Resolves to a response containing the ID of the insight which was deleted.
 * @private
 *
 * @example  <caption>In the example below, the API deletes an existing insight rule and returns a Promise that resolves to a response containing the ID</caption>
 * const insightID = "<some GUID>";
 * PCore.getAnalyticsUtils().deleteInsight(insightID).then(response => { console.log(response.data); }).catch(() => { ... });
 */
export function deleteInsight(insightID: string): Promise<object> {
  const reqPayload = { insightID };
  const { restApi, reqType } = resolveRestApi(DELETE_INSIGHT, reqPayload);
  return axiosFetch(restApi, {
    method: reqType
  });
}

/**
 * Exports a table to an Excel file.
 * @function exportToExcel
 * @param exportRequest A payload object to configure an export
 * @param exportRequest.pyName Gives the exported table a name.
 * @param exportRequest.pyMode The type of export to perform ("excel", etc.)
 * @param exportRequest.pyClassName The class of the data to export.
 * @param exportRequest.pyReportTitle The name of the file to export.
 * @param exportRequest.pyContent A stringified JSON object containing table component <code>metadata</code> and <code>state</code>.
 * @returns Resolves to a base64 response that is the Excel file.
 * @private
 *
 * @example  <caption>In the example below, the API returns a Promise that resolves to a base64 response (the Excel file).</caption>
 * const exportRequest = {
 *   pyName: "Export",
 *   pyMode: "excel",
 *   pyClassName: "@baseclass",
 *   pyReportTitle: "Insight1",
 *   pyContent: JSON.stringify({ metadata, state })
 * };
 * PCore.getAnalyticsUtils().exportToExcel(exportRequest).then(response => { ... }).catch(() => { ... });
 */
export function exportToExcel(exportRequest: {
  pyName: string;
  pyMode: string;
  pyClassName: string;
  pyReportTitle: string;
  pyContent: string;
}) {
  const { restApi, reqType } = resolveRestApi(EXPORT_TO_EXCEL);
  return axiosFetch(restApi, {
    method: reqType,
    body: JSON.stringify(exportRequest)
  });
}

/**
 * Exports a table to a CSV file.
 * @function exportToCSV
 * @param exportRequest A payload object to configure an export
 * @param exportRequest.pyName Gives the exported table a name.
 * @param exportRequest.pyMode The type of export to perform ("csv", etc.)
 * @param exportRequest.pyClassName The class of the data to export.
 * @param exportRequest.pyReportTitle The name of the file to export.
 * @param exportRequest.pyContent A stringified JSON object containing table component <code>metadata</code> and <code>state</code>.
 * @returns Resolves to a base64 response that is the Excel file.
 * @private
 *
 * @example  <caption>In the example below, the API returns a Promise that resolves to a base64 response (the CSV file).</caption>
 * const exportRequest = {
 *   pyName: "Export",
 *   pyMode: "excel",
 *   pyClassName: "@baseclass",
 *   pyReportTitle: "Insight1",
 *   pyContent: JSON.stringify({ metadata, state })
 * };
 * PCore.getAnalyticsUtils().exportToCSV(exportRequest).then(response => { ... }).catch(() => { ... });
 */
export function exportToCSV(exportRequest: {
  pyName: string;
  pyMode: string;
  pyClassName: string;
  pyReportTitle: string;
  pyContent: string;
}) {
  const { restApi, reqType } = resolveRestApi(EXPORT_TO_CSV);
  return axiosFetch(restApi, {
    method: reqType,
    body: JSON.stringify(exportRequest)
  });
}

/**
 * Schedules a table insight as an Excel file.
 * @function scheduleInsight
 * @param insightID The ID of the insight related to the schedule
 * @param exportRequest A payload object to configure an export
 * @param exportRequest.pyName Gives the exported table a name.
 * @param exportRequest.pyMode The type of export to perform ("excel", etc.)
 * @param exportRequest.pyClassName The class of the data to export.
 * @param exportRequest.pyReportTitle The name of the file to export.
 * @param exportRequest.pyContent A stringified JSON object containing table component <code>metadata</code> and <code>state</code>.
 * @private
 *
 * @example  <caption>In the example below, the API schedules an insight.</caption>
 * const exportRequest = {
 *   pyName: "Export",
 *   pyMode: "excel",
 *   pyClassName: "@baseclass",
 *   pyReportTitle: "Insight1",
 *   pyContent: JSON.stringify({ metadata, state })
 * };
 * PCore.getAnalyticsUtils().scheduleInsight(exportRequest).then(response => { ... }).catch(() => { ... });
 */
export const scheduleInsight = (
  insightID: string,
  exportRequest: { pyName: string; pyMode: string; pyClassName: string; pyReportTitle: string; pyContent: string }
) => {
  const reqPayload = { insightID };
  const { restApi, reqType } = resolveRestApi(SCHEDULE_INSIGHT, reqPayload);
  return axiosFetch(restApi, {
    method: reqType,
    body: JSON.stringify(exportRequest)
  });
};

/**
 * Updates a scheduled table insight
 * @function updateScheduledInsight
 * @param insightID The ID of the insight related to the schedule
 * @param scheduleID The ID of the schedule being updated
 * @param exportRequest A payload object to configure an export
 * @param exportRequest.pyName Gives the exported table a name.
 * @param exportRequest.pyMode The type of export to perform ("excel", etc.)
 * @param exportRequest.pyClassName The class of the data to export.
 * @param exportRequest.pyReportTitle The name of the file to export.
 * @param exportRequest.pyContent A stringified JSON object containing table component <code>metadata</code> and <code>state</code>.
 * @param exportRequest.pyID Key for scheduled insight to update
 * @private
 *
 * @example  <caption>In the example below, the API updates a scheduled insight.</caption>
 * const exportRequest = {
 *   pyName: "Export",
 *   pyMode: "excel",
 *   pyClassName: "@baseclass",
 *   pyReportTitle: "Insight 1",
 *   pyContent: JSON.stringify({ metadata, state })
 *   pyID: 'W-8001'
 * };
 * PCore.getAnalyticsUtils().updateScheduledInsight(insightID, scheduleID, exportRequest).then(response => { ... }).catch(() => { ... });
 */
export const updateScheduledInsight = (
  insightID: string,
  scheduleID: string,
  exportRequest: {
    pyName: string;
    pyMode: string;
    pyClassName: string;
    pyReportTitle: string;
    pyContent: string;
    pyID: string;
  }
) => {
  const reqPayload = { insightID, scheduleID };
  const { restApi, reqType } = resolveRestApi(UPDATE_SCHEDULED_INSIGHT, reqPayload);
  return axiosFetch(restApi, {
    method: reqType,
    body: JSON.stringify(exportRequest)
  });
};

/**
 * Deletes a scheduled table insight
 * @function deleteScheduledInsight
 * @param insightID The ID of the insight related to the schedule
 * @param scheduleID The ID of the schedule being deleted
 * @private
 *
 * @example  <caption>In the example below, the API deletes an insight.</caption>
 * PCore.getAnalyticsUtils().deleteScheduledInsight(insightId, scheduleID).then(response => { ... }).catch(() => { ... });
 */
export const deleteScheduledInsight = (insightID: string, scheduleID: string) => {
  const reqPayload = { insightID, scheduleID };
  const { restApi, reqType } = resolveRestApi(DELETE_SCHEDULED_INSIGHT, reqPayload);
  return axiosFetch(restApi, {
    method: reqType
  });
};

/**
 * Translates a list of strings.
 * @function translateStrings
 * @param stringsToTranslate A list of strings to translate.
 * @returns Resolves to a response containing a grouping of key/value pairs.
 *
 * @example  <caption>In the example below, the API returns a Promise that resolves to a response containing a grouping of key/value pairs. </caption>
 * const stringsToTranslate = ["Hello", "Thank you"];
 * PCore.getAnalyticsUtils().translateStrings(stringsToTranslate).then(response => { console.log(response.data); }).catch(() => { ... });
 * // console output: { Hello: "Bonjour", "Thank you": "Merci" }
 */
export function translateStrings(stringsToTranslate: string): Promise<object> {
  const { restApi, reqType } = resolveRestApi(TRANSLATE_STRINGS);
  return axiosFetch(restApi, {
    method: reqType,
    body: JSON.stringify(stringsToTranslate)
  });
}
export default {
  getDataObjects,
  getDataPageObjects,
  getDataViewMetadata,
  getDefaultColumns,
  getFieldsForDataSource,
  getPrimaryFieldsForDataSource,
  getPrimaryFieldsForInsights,
  getInsightIDs,
  getInsightByID,
  getInsightsByID,
  createInsight,
  updateInsight,
  deleteInsight,
  exportToExcel,
  exportToCSV,
  scheduleInsight,
  updateScheduledInsight,
  deleteScheduledInsight,
  translateStrings
};
